import { useRef, useState, useMemo, ReactNode } from 'react';
import { getEntityRelations } from '@backstage/plugin-catalog-react';
import {
  Entity,
  GroupEntity,
  RELATION_OWNED_BY,
} from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import Fab from '@mui/material/Fab';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EmailIcon from '@mui/icons-material/Email';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { SlackIcon, JiraIcon } from '@xlrt/components';

import { useSupportButtonStyles } from './support-button.styles';
import { SupportList, SupportOption } from './SupportList';
import { catalogTranslationRef } from '../../utils/translation';
import { useFetchEntityByRef } from '../../hooks/useFetchEntityByRef';
import { GitHubIcon } from '@backstage/core-components';

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const getTypeFromUrl = (url: string): SupportOption['type'] | null => {
  const trimmedUrl = url.trim();
  const lowerUrl = trimmedUrl.toLowerCase();

  if (lowerUrl.startsWith('mailto:')) {
    const email = trimmedUrl.substring(7); // Remove 'mailto:' prefix
    return isValidEmail(email) ? 'email' : null;
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(trimmedUrl);
  } catch {
    return null;
  }

  const protocol = parsedUrl.protocol.toLowerCase();
  if (protocol !== 'http:' && protocol !== 'https:') {
    return null;
  }

  const hostname = parsedUrl.hostname.toLowerCase();
  const pathname = parsedUrl.pathname.toLowerCase();

  if (hostname === 'slack.com' || hostname.endsWith('.slack.com')) {
    return 'slack';
  }

  if (
    hostname === 'devstack.vwgroup.com' &&
    pathname.startsWith('/jira/servicedesk')
  ) {
    return 'jira';
  }

  if (
    ['github.com', 'git.hub.vwgroup.com', 'cariad.ghe.com'].includes(hostname)
  ) {
    return 'githubIssues';
  }

  if (
    hostname === 'sc3.vwgroup.com' ||
    hostname === 'myserveprod.service-now.com'
  ) {
    return 'ticket';
  }

  return null;
};

const getIcon = (type: SupportOption['type']): ReactNode => {
  switch (type) {
    case 'email':
      return <EmailIcon />;
    case 'slack':
      return <SlackIcon />;
    case 'jira':
      return <JiraIcon />;
    case 'githubIssues':
      return <GitHubIcon />;
    case 'ticket':
      return <QuestionAnswerIcon />;
    default:
      return <QuestionAnswerIcon />;
  }
};

const getTypeSortPriority = (type: SupportOption['type']): number => {
  const priorityMap: Record<SupportOption['type'], number> = {
    email: 1,
    slack: 2,
    jira: 3,
    ticket: 4,
    githubIssues: 5,
    generic: 6,
  };
  return priorityMap[type];
};

export interface SupportButtonProps {
  entity: Entity;
}

export const SupportButton = ({ entity }: SupportButtonProps) => {
  const { t } = useTranslationRef(catalogTranslationRef);
  const classes = useSupportButtonStyles();
  const fabRef = useRef<HTMLButtonElement>(null);
  const [openModal, setOpenModal] = useState(false);

  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  const {
    value: groupEntityVal,
    error: groupEntityError,
    loading: groupEntityLoading,
  } = useFetchEntityByRef(ownedByRelations[0]);

  let groupEntity: GroupEntity | null = null;
  if (!groupEntityLoading && !groupEntityError && groupEntityVal) {
    groupEntity = groupEntityVal as GroupEntity;
  }

  const supportOptions = useMemo(() => {
    const options: SupportOption[] = [];
    const seenUrls = new Set<string>();

    // Add group email if it's valid
    if (groupEntity) {
      const groupEmail = groupEntity.spec.profile?.email?.trim();
      if (groupEmail && isValidEmail(groupEmail)) {
        const mailtoUrl = `mailto:${groupEmail}`;
        options.push({
          title: 'Group Email',
          url: mailtoUrl,
          type: 'email',
          icon: getIcon('email'),
        });
        seenUrls.add(mailtoUrl.toLowerCase());
      }

      // Process groupEntity metadata links
      const groupLinks = groupEntity.metadata.links || [];
      groupLinks.forEach(link => {
        if (!link.url) return;

        const linkUrlLower = link.url.toLowerCase().trim();
        const linkUrl = link.url.trim();
        if (seenUrls.has(linkUrlLower)) return;

        const type = getTypeFromUrl(linkUrl);
        if (type) {
          const title = link.title?.trim() || linkUrl;
          options.push({
            title,
            url: linkUrl,
            type,
            icon: getIcon(type),
          });
          seenUrls.add(linkUrlLower);
        }
      });
    }

    if (entity && entity.kind.toLocaleLowerCase('en-US') === 'group') {
      const currentGroupEntity = structuredClone(entity) as GroupEntity;
      const groupEmail = currentGroupEntity.spec.profile?.email?.trim();
      if (groupEmail && isValidEmail(groupEmail)) {
        const mailtoUrl = `mailto:${groupEmail}`;
        options.push({
          title: 'Group Email',
          url: mailtoUrl,
          type: 'email',
          icon: getIcon('email'),
        });
        seenUrls.add(mailtoUrl.toLowerCase());
      }
    }

    // Process entity metadata links (skip duplicates)
    const entityLinks = entity?.metadata.links || [];
    entityLinks.forEach(link => {
      if (!link.url) return;

      const linkUrlLower = link.url.toLowerCase().trim();
      const linkUrl = link.url.trim();
      if (seenUrls.has(linkUrlLower)) return;

      const type = getTypeFromUrl(linkUrl);
      if (type) {
        const title = link.title?.trim() || linkUrl;
        options.push({
          title,
          url: linkUrl,
          type,
          icon: getIcon(type),
        });
        seenUrls.add(linkUrlLower);
      }
    });

    // Sort by type priority
    options.sort(
      (a, b) => getTypeSortPriority(a.type) - getTypeSortPriority(b.type),
    );

    return options;
  }, [groupEntity, entity]);

  const handleClick = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return supportOptions.length > 0 ? (
    <>
      <Fab
        ref={fabRef}
        variant="extended"
        aria-label="support"
        className={`${classes.floatingButton} ${classes.actionButton} ${openModal ? 'listOpen' : ''}`}
        onClick={handleClick}
      >
        <ChatBubbleIcon className={classes.icon} />
        <span className={classes.label}>{t('support.supportButtonLabel')}</span>
      </Fab>
      <SupportList
        open={openModal}
        onClose={handleCloseModal}
        anchorEl={fabRef.current}
        supportOptions={supportOptions}
      />
    </>
  ) : null;
};
